import { type AdminUserSummary, type AuditFilters, apiClient } from '@/api';
import { formatRelativeAge, formatTokenCount } from '@/lib/format';
import { formatCountdown } from '@/lib/tempAccount';
import {
  type SortKey,
  accountDay,
  formatUserCost,
  visibleUsers
} from '@/lib/userStats';
import { cn } from '@/lib/utils';
import { ArrowDown, ArrowUp, ScrollText } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';

import {
  adminUserDetailDialogOpenState,
  adminUserDetailIdentifierState,
  adminUsersState
} from '@/state/project';

// A header with several sortKeys cycles through them on each click, so "Created"
// toggles newest <-> oldest instead of committing to one direction.
const COLUMNS: {
  label: string;
  sortKeys: SortKey[];
  className?: string;
}[] = [
  { label: 'User', sortKeys: ['identifier'] },
  { label: 'Role', sortKeys: [] },
  { label: 'Created', sortKeys: ['newest', 'oldest'] },
  { label: 'Last active', sortKeys: ['recent-login'] },
  { label: 'Tokens', sortKeys: ['tokens'], className: 'text-right' },
  { label: 'Cost', sortKeys: [], className: 'text-right' },
  { label: '', sortKeys: [], className: 'text-right' }
];

const ASCENDING_KEYS: SortKey[] = ['oldest', 'identifier'];

const USERS_PER_PAGE = 20;

type Props = {
  onDrilldown: (patch: Partial<AuditFilters>) => void;
};

/**
 * Read-only user lookup for the audit dialog's Users tab.
 *
 * Separate from `UserTable`, which carries the ten row mutations (role, disable,
 * purge, delete...) that have no place in an audit view. This one only looks up:
 * click a row for the detail dialog, or jump to the Events tab filtered to that
 * user. Reads the same `adminUsersState` the admin pane fills, and fetches once
 * itself if opened before that happened.
 */
export default function AuditUsersTable({ onDrilldown }: Props) {
  const [users, setUsers] = useRecoilState(adminUsersState);
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('recent-login');
  const [page, setPage] = useState(1);
  const setDetailIdentifier = useSetRecoilState(adminUserDetailIdentifierState);
  const setDetailOpen = useSetRecoilState(adminUserDetailDialogOpenState);

  useEffect(() => {
    if (users.length) return;
    apiClient
      .listAdminUsers()
      .then((response) => setUsers(response.users))
      .catch(() => toast.error('Could not load user list'));
  }, [users.length, setUsers]);

  const rows = useMemo(
    () => visibleUsers(users, { query, filter: 'all', sortKey }),
    [users, query, sortKey]
  );

  const pageCount = Math.max(1, Math.ceil(rows.length / USERS_PER_PAGE));
  const currentPage = Math.min(page, pageCount);
  const pageRows = rows.slice(
    (currentPage - 1) * USERS_PER_PAGE,
    currentPage * USERS_PER_PAGE
  );

  useEffect(() => {
    setPage(1);
  }, [query, sortKey]);

  const openDetail = (row: AdminUserSummary) => {
    setDetailIdentifier(row.identifier);
    setDetailOpen(true);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search users…"
          autoComplete="off"
          className="h-8 min-w-[12rem] flex-1"
        />
        <span className="text-[10px] text-muted-foreground">
          {pageRows.length.toLocaleString()} of {rows.length.toLocaleString()}
        </span>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {COLUMNS.map((column) => {
                const at = column.sortKeys.indexOf(sortKey);
                const active = at >= 0;
                return (
                  <TableHead
                    key={column.label || 'actions'}
                    className={cn('h-8', column.className)}
                  >
                    {column.sortKeys.length ? (
                      <button
                        type="button"
                        onClick={() =>
                          setSortKey(
                            column.sortKeys[(at + 1) % column.sortKeys.length]
                          )
                        }
                        className={cn(
                          'inline-flex items-center gap-1 hover:text-foreground',
                          active && 'text-foreground'
                        )}
                      >
                        {column.label}
                        {active ? (
                          ASCENDING_KEYS.includes(sortKey) ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : (
                            <ArrowDown className="h-3 w-3" />
                          )
                        ) : null}
                      </button>
                    ) : (
                      column.label
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.map((row) => (
              <TableRow
                key={row.id}
                role="button"
                tabIndex={0}
                onClick={() => openDetail(row)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openDetail(row);
                  }
                }}
                className="cursor-pointer"
              >
                <TableCell className="max-w-[18rem] py-1.5">
                  <div className="truncate font-medium">{row.identifier}</div>
                  <span className="text-[10px] text-muted-foreground">
                    {row.provider || 'unknown provider'}
                  </span>
                </TableCell>
                <TableCell className="py-1.5">
                  <div className="flex flex-wrap items-center gap-1">
                    {row.role === 'admin' ? (
                      <Badge className="text-[10px]">admin</Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        user
                      </span>
                    )}
                    {row.disabled ? (
                      <Badge variant="destructive" className="text-[10px]">
                        disabled
                      </Badge>
                    ) : null}
                    {row.is_temporary ? (
                      <Badge variant="secondary" className="text-[10px]">
                        temp · {formatCountdown(row.temp_expires_at)}
                      </Badge>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell className="py-1.5 font-mono text-xs tabular-nums text-muted-foreground">
                  {accountDay(row.createdAt) ?? '—'}
                </TableCell>
                <TableCell className="py-1.5 text-xs tabular-nums">
                  {row.last_login_at ? (
                    formatRelativeAge(row.last_login_at)
                  ) : (
                    <span className="text-muted-foreground">never</span>
                  )}
                </TableCell>
                <TableCell className="py-1.5 text-right font-mono text-xs tabular-nums">
                  {formatTokenCount(row.total_tokens ?? 0)}
                </TableCell>
                <TableCell className="py-1.5 text-right font-mono text-xs tabular-nums">
                  {formatUserCost(row)}
                </TableCell>
                <TableCell
                  className="py-1.5 text-right"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 gap-1 px-2 text-[10px]"
                    onClick={() =>
                      onDrilldown({ user_identifier: row.identifier })
                    }
                    title="Show this user's events in the audit log"
                  >
                    <ScrollText className="h-3 w-3" />
                    Audit trail
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {pageCount > 1 ? (
        <div className="flex items-center justify-between gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-[10px]"
            onClick={() => setPage(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            Previous
          </Button>
          <span className="text-[10px] text-muted-foreground">
            Page {currentPage} of {pageCount}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-[10px]"
            onClick={() => setPage(currentPage + 1)}
            disabled={currentPage >= pageCount}
          >
            Next
          </Button>
        </div>
      ) : null}

      {users.length > 0 && rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No users match this search.
        </p>
      ) : null}
    </div>
  );
}
